import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { PubNubProvider } from 'pubnub-react'
import Pubnub, { MessageEvent, StatusEvent } from 'pubnub'
import { Header } from '../Header'
import { ConfirmModal } from '../ConfirmModal'
import { MobileTopBar } from '../Header/MobileTopBar'
import { SideBar } from '../SideBar'
import { IosInstallBanner } from '../IosInstallBanner'
import { EnableNotificationsBanner } from '../EnableNotificationsBanner'
import { SideBarMobileDrawer } from '../SideBar/MobileDrawer'
import { UnreadCountInvalidator } from './UnreadCountInvalidator'
import { useAuth } from '../../contexts/AuthContext'
import {
  useCurrentUser,
  useDebouncedInvalidateTotalUnread,
  useDebouncedRefetchNotificationSummary,
  useMe,
  useNotificationSummary
} from '../../hooks'
import {
  clearActivePubnubClient,
  setActivePubnubClient,
} from '../../lib/activePubnubClient'
import { useTermsStatus } from '../../hooks/useTermsStatus'
import { buildEntryUrl } from '../../utils/buildEntryUrl'
import { setSentryUser } from '../../configs'
import { organizationStrings } from '../../constants/strings'
import useToast from '../../hooks/useToast'
import { isChatRoutePath } from '../../constants/routes'
import { useOrganizationScopeOrNull } from '../OrganizationScope/context'
import { getTrustedRecallMessageNotification, isIncomingChannelNotification, registerTrustedRecallPublisher } from '../../utils/chatRecallMessage'
import { parseOrganizationNavigationMessage } from '../../utils/notificationNavigation'
import {
  diffPersonalChannels,
  findPersonalChannelOrganizationId,
  resolvePersonalChannels,
} from '../../utils/personalChannels'

type AppLayoutProps = {
  children: ReactNode
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user, signOut } = useAuth()
  const organizationScope = useOrganizationScopeOrNull()
  const scopeOrganizationId = organizationScope?.organizationId ?? null
  const navigate = useNavigate()
  const location = useLocation()
  const { data: currentUser } = useCurrentUser()
  const { data: termsStatus } = useTermsStatus()
  // 利用規約の同意前（同意必須ONかつ未同意）は PubNub 接続・通知登録を開始しない。
  // 運営の動作確認中（OFF）はチャット検証のため PubNub を使えるが、通知の端末登録はしない
  const businessAccessAllowed = termsStatus?.businessAccessAllowed === true
  const notificationRegistrationAllowed =
    termsStatus?.notificationRegistrationAllowed === true
  const [error, setError] = useState<string | undefined>()
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const pubnub = useMemo(
    () =>
      currentUser?.uuid && businessAccessAllowed
        ? new Pubnub({
          publishKey: process.env.REACT_APP_PUBNUB_PUBLISH_KEY || '',
          subscribeKey: process.env.REACT_APP_PUBNUB_SUBSCRIBE_KEY || '',
          uuid: currentUser.uuid,
        })
        : null,
    [currentUser?.uuid, businessAccessAllowed]
  )

  const clientId = useMemo(() => {
    const storedClientId = localStorage.getItem('clientId') || `${Date.now()}`
    localStorage.setItem('clientId', storedClientId)
    return storedClientId
  }, [])

  const entryUrl = useMemo(() => {
    if (!currentUser?.organizationEntryKey) return undefined
    return buildEntryUrl(currentUser.organizationEntryKey)
  }, [currentUser?.organizationEntryKey])

  useEffect(() => {
    if (currentUser?.pubnubSystemBackendId) {
      registerTrustedRecallPublisher(currentUser.pubnubSystemBackendId)
    }
  }, [currentUser?.pubnubSystemBackendId])

  const { data: me } = useMe()
  const { data: notificationSummary } = useNotificationSummary()
  const { scheduleRefetch: scheduleSummaryRefetch } =
    useDebouncedRefetchNotificationSummary()

  const invalidateTotalUnread = useDebouncedInvalidateTotalUnread()

  const handleSignOut = async () => {
    setIsSigningOut(true)
    setError(undefined)

    const errorMessage = await signOut()
    if (errorMessage) {
      setError(errorMessage)
    }
    setIsSigningOut(false)
  }

  // 通知タップ: SW は検証済みの組織スコープ付き遷移だけを postMessage する（設計書 §9.6）。
  const { toastWarning } = useToast()
  const [isSwitchingForNotification, setIsSwitchingForNotification] =
    useState(false)
  const [pendingNotification, setPendingNotification] = useState<{
    organizationId: number
    path: string
  } | null>(null)

  const switchForNotification = useCallback(
    async (navigation: { organizationId: number; path: string }) => {
      if (!organizationScope) return
      setIsSwitchingForNotification(true)
      const status = await organizationScope.switchOrganization(
        navigation.organizationId,
        { path: navigation.path },
      )
      setIsSwitchingForNotification(false)

      if (status === 'busy') {
        toastWarning(organizationStrings.switcher.busy)
        return
      }
      if (status === 'unsaved-input') {
        // OrganizationSwitcher と同じく、破棄の確認を取ってから再実行する
        setPendingNotification(navigation)
      }
    },
    [organizationScope, toastWarning],
  )

  const discardAndSwitchForNotification = useCallback(async () => {
    if (!pendingNotification || !organizationScope) return
    setIsSwitchingForNotification(true)
    const status = await organizationScope.switchOrganization(
      pendingNotification.organizationId,
      { path: pendingNotification.path, discardUnsavedInput: true },
    )
    setIsSwitchingForNotification(false)

    if (status === 'busy') {
      toastWarning(organizationStrings.switcher.busy)
      return
    }
    setPendingNotification(null)
  }, [organizationScope, pendingNotification, toastWarning])

  useEffect(() => {
    const serviceWorker = navigator.serviceWorker
    if (!serviceWorker) return

    const handleServiceWorkerMessage = (event: { data: unknown }) => {
      const navigation = parseOrganizationNavigationMessage(event.data)
      if (!navigation) return

      if (navigation.organizationId === scopeOrganizationId) {
        navigate(navigation.path)
        return
      }

      // 別組織の通知は実行中操作と未保存入力を確認しながら切り替えてから遷移する
      void switchForNotification(navigation)
    }

    serviceWorker.addEventListener('message', handleServiceWorkerMessage)

    return () => {
      serviceWorker.removeEventListener('message', handleServiceWorkerMessage)
    }
  }, [navigate, scopeOrganizationId, switchForNotification])

  // 全 ACTIVE 所属の個人UUIDチャネルを1つのクライアントで購読する（設計書 §9.8）。
  const personalChannels = useMemo(
    () =>
      resolvePersonalChannels(
        me?.memberships,
        notificationSummary?.organizations
      ),
    [me?.memberships, notificationSummary?.organizations]
  )

  const subscribedChannelsRef = useRef<string[]>([])
  const handlePersonalChannelMessageRef = useRef<(event: MessageEvent) => void>(
    () => {}
  )
  const scheduleSummaryRefetchRef = useRef(scheduleSummaryRefetch)

  const handlePersonalChannelMessage = useCallback(
    (event: MessageEvent) => {
      // 自分の送信のエコー（別組織のタブから送った所属の UUID を含む）はどの組織の未読も変えない
      if (me?.memberships.some((m) => m.memberUuid === event.publisher)) return

      // 組織は購読チャネル（所属の memberUuid）で決める。所属に無いチャネルは選択中と見なさない
      const channelOrganizationId = findPersonalChannelOrganizationId(
        me?.memberships,
        event.channel
      )
      const isSelectedOrganization =
        channelOrganizationId !== null &&
        channelOrganizationId === scopeOrganizationId

      if (isSelectedOrganization) {
        if (
          getTrustedRecallMessageNotification(event.message, event.publisher)
        ) {
          invalidateTotalUnread()
          return
        }
        if (isIncomingChannelNotification(event.message)) {
          invalidateTotalUnread()
        }
      }

      // 選択中かどうかに関わらず、切り替えメニューの未読数はサマリーから更新する
      scheduleSummaryRefetch()
    },
    [
      invalidateTotalUnread,
      me?.memberships,
      scheduleSummaryRefetch,
      scopeOrganizationId,
    ]
  )

  useEffect(() => {
    handlePersonalChannelMessageRef.current = handlePersonalChannelMessage
    scheduleSummaryRefetchRef.current = scheduleSummaryRefetch
  }, [handlePersonalChannelMessage, scheduleSummaryRefetch])

  // クライアントの寿命は組織スコープ1回分。ハンドラは ref 経由で読むので購読は落ちない。
  useEffect(() => {
    if (!pubnub) return

    const listenerParams = {
      message: (event: MessageEvent) => {
        handlePersonalChannelMessageRef.current(event)
      },
      status: (event: StatusEvent) => {
        // 再接続後はサーバ側の未読を引き直す
        if (event.category === Pubnub.CATEGORIES.PNReconnectedCategory) {
          scheduleSummaryRefetchRef.current()
        }
      },
    }

    pubnub.addListener(listenerParams)
    setActivePubnubClient(pubnub)

    return () => {
      pubnub.removeListener(listenerParams)
      // 組織切り替えとログアウトでネットワークを止め、旧組織の購読を残さない。
      pubnub.stop()
      clearActivePubnubClient(pubnub)
      subscribedChannelsRef.current = []
    }
  }, [pubnub])

  // 購読セットの差分更新: 増えた分だけ購読し、消えた組織だけ解除する
  useEffect(() => {
    if (!pubnub) return

    const subscribed = subscribedChannelsRef.current
    const { added, removed } = diffPersonalChannels(subscribed, personalChannels)

    if (added.length > 0) pubnub.subscribe({ channels: added })
    if (removed.length > 0) pubnub.unsubscribe({ channels: removed })
    subscribedChannelsRef.current = personalChannels
  }, [personalChannels, pubnub])

  // 画面へ復帰したときもサマリーを引き直す（5分ごとの再取得は query 側で行う）
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      scheduleSummaryRefetch()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [scheduleSummaryRefetch])

  useEffect(() => {
    if (currentUser) {
      setSentryUser({
        userId: String(currentUser.id),
        nickname: currentUser.name,
        clientId,
        accountId: currentUser.accountId,
        organizationId: scopeOrganizationId,
      });
    }
  }, [currentUser, clientId, scopeOrganizationId]);

  useEffect(() => {
    setIsMobileNavOpen(false)
  }, [location.pathname, location.search])

  // チャット画面でのみ、ソフトキーボード表示時に visualViewport の実高さを CSS 変数へ
  // 反映する。chat-section-h と body 固定がこの値を使う。非チャット画面では更新しない
  // （一覧の検索やフォーム入力でキーボードを開いてもシェルを縮ませないため）。
  useEffect(() => {
    if (!isChatRoutePath(location.pathname)) return
    const viewport = window.visualViewport
    if (!viewport) return
    const root = document.documentElement
    const updateViewportHeight = () => {
      root.style.setProperty('--app-vvh', `${viewport.height}px`)
      root.style.setProperty('--app-vvtop', `${viewport.offsetTop}px`)
    }
    // iOS は visualViewport.resize の発火が遅れる/取りこぼすことがあるため、
    // フォーカス変化後にも遅延付きで再計測し、キーボード高さを確実に反映する。
    let pendingTimer: number | undefined
    const updateSoon = () => {
      updateViewportHeight()
      window.clearTimeout(pendingTimer)
      pendingTimer = window.setTimeout(updateViewportHeight, 300)
    }
    updateViewportHeight()
    viewport.addEventListener('resize', updateViewportHeight)
    viewport.addEventListener('scroll', updateViewportHeight)
    window.addEventListener('focusin', updateSoon)
    window.addEventListener('focusout', updateSoon)
    return () => {
      window.clearTimeout(pendingTimer)
      viewport.removeEventListener('resize', updateViewportHeight)
      viewport.removeEventListener('scroll', updateViewportHeight)
      window.removeEventListener('focusin', updateSoon)
      window.removeEventListener('focusout', updateSoon)
      root.style.removeProperty('--app-vvh')
      root.style.removeProperty('--app-vvtop')
    }
  }, [location.pathname])

  // チャット画面ではページ自体のスクロールを止め、高さを visualViewport に固定する。
  // iOS はキーボード表示時、レイアウトビューポート(キーボードを無視した高さ)基準で
  // ページを自動スクロールし、入力欄の下に余白を見せてしまう。body を fixed にして
  // ページスクロールを根絶し、html のスクロール領域(余白)を発生させない。
  useEffect(() => {
    if (!isChatRoutePath(location.pathname)) return
    const body = document.body
    const savedStyle = body.getAttribute('style') || ''
    // body:fixed はページのスクロール位置を失わせるため、離脱時に復元できるよう保存する
    const scrollY = window.scrollY
    body.style.position = 'fixed'
    body.style.top = 'var(--app-vvtop, 0px)'
    body.style.left = '0'
    body.style.right = '0'
    body.style.height = 'var(--app-vvh, 100%)'
    body.style.overflow = 'hidden'
    return () => {
      body.setAttribute('style', savedStyle)
      // fixed 解除後に元のスクロール位置へ戻す（戻り先ページがトップにリセットされるのを防ぐ）
      window.scrollTo(0, scrollY)
    }
  }, [location.pathname])

  if (!user) return null

  const isChatPage = isChatRoutePath(location.pathname)

  const layoutContent = (
    <>
      <SideBar
        error={error}
        isSigningOut={isSigningOut}
        onSignOut={handleSignOut}
      />
      <SideBarMobileDrawer
        open={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
        email={user.email ?? undefined}
        error={error}
        isSigningOut={isSigningOut}
        onSignOut={handleSignOut}
      />

      <div className="flex-1 lg:ml-[260px]">
        <MobileTopBar onOpenMobileNav={() => setIsMobileNavOpen(true)} />
        <Header
          email={user.email ?? undefined}
          organizationName={currentUser?.organizationName}
          entryUrl={entryUrl}
        />
        <div className="px-[var(--app-px)] pb-[var(--app-pb)] pt-[var(--app-pt)] lg:px-10 lg:pb-12">
          {/* チャット画面は高さを画面に合わせて body を固定するため、上にバナーが出ると
              入力欄が画面外に押し出されて押せなくなる。チャット画面では表示しない。
              アンマウントせず隠すのは、閉じた状態と、通知許可済み端末の購読の再登録
              （useWebPush）をチャット画面の前後でも保つため */}
          <div hidden={isChatPage}>
            <IosInstallBanner />
            <EnableNotificationsBanner
              enabled={Boolean(currentUser?.id) && notificationRegistrationAllowed}
            />
          </div>
          {children}
        </div>
      </div>
    </>
  )

  return (
    <main className="relative min-h-screen overflow-x-clip bg-surface text-ink">
      <div className="relative flex min-h-screen flex-col">
        {pubnub ? (
          <PubNubProvider client={pubnub}>
            <UnreadCountInvalidator />
            {layoutContent}
          </PubNubProvider>
        ) : (
          layoutContent
        )}
      </div>
      <ConfirmModal
        open={pendingNotification !== null}
        textTitle={organizationStrings.switcher.unsavedTitle}
        textMessage={organizationStrings.switcher.unsavedMessage}
        textCancel={organizationStrings.switcher.unsavedCancel}
        textConfirm={organizationStrings.switcher.unsavedConfirm}
        isLoading={isSwitchingForNotification}
        onClose={() => setPendingNotification(null)}
        onConfirm={() => void discardAndSwitchForNotification()}
      />
    </main>
  )
}
